import { ChangeEvent } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Iconify from '../../../components/iconify';

type Props = {
  /** Key used for this tile in the parent's image map, e.g. `input1`. */
  inputId: string;
  /** Preview of the image on this tile (object URL, or the saved R2 URL). */
  src?: string;
  /** A signed-off record is read-only: show the image but don't allow a change. */
  disabled?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>, inputId: string) => void;
};

/**
 * One image slot on the Pre-Screening screen (Visuals / OCT / Fundus).
 * Shows the chosen image, or an "Add an image" placeholder when empty,
 * instead of a broken <img src="">.
 */
export default function PrescreeningImageTile({
  inputId,
  src,
  disabled = false,
  onChange,
}: Props) {
  const htmlId = `imageInput-${inputId}`;

  return (
    <Box
      sx={{
        width: '100%',
        height: '150px',
        margin: '10px',
        borderRadius: '15px',
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <label
        htmlFor={htmlId}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt="Preview"
            sx={{ width: 150, height: 112.5, mb: 1, objectFit: 'contain' }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ color: 'text.disabled', mb: 1 }}
          >
            <Iconify icon="eva:image-outline" width={48} />
            <Typography variant="body2">
              {disabled ? 'No image' : 'Add an image'}
            </Typography>
          </Stack>
        )}
      </label>
      {!disabled && (
        <>
          <label htmlFor={htmlId}>
            <Typography gutterBottom variant="body2" sx={{ cursor: 'pointer' }}>
              Select Image
            </Typography>
          </label>
          <input
            type="file"
            id={htmlId}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => onChange(e, inputId)}
          />
        </>
      )}
    </Box>
  );
}
